import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const CODE = 'KBCHARMGIFT';
const CART_URL = '/cart.js';
const CART_UPDATE_URL = Theme?.routes?.cart_update_url || '/cart/update.js';

/** @type {boolean} */
let syncing = false;
/** @type {Promise<void> | null} */
let pending = null;

/**
 * @param {{product_type?: string, product_title?: string, title?: string, handle?: string}} item
 */
function isCharmItem(item) {
  const type = String(item.product_type || '').toLowerCase();
  const title = String(item.product_title || item.title || '').toLowerCase();
  const handle = String(item.handle || '').toLowerCase();
  return type.includes('charm') || title.includes('charm') || handle.includes('charm') || title.includes('scrunchie');
}

/**
 * @param {{product_type?: string, product_title?: string, title?: string}} item
 */
function isBagItem(item) {
  if (isCharmItem(item)) return false;
  const type = String(item.product_type || '').toLowerCase();
  const title = String(item.product_title || item.title || '').toLowerCase();
  if (type.includes('bag') || type.includes('tote') || type.includes('clutch')) return true;
  return (
    title.includes('bag') ||
    title.includes('tote') ||
    title.includes('clutch') ||
    title.includes('hobo') ||
    title.includes('bucket')
  );
}

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id], cart-discount-component[data-section-id]'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter((id, index, list) => Boolean(id) && list.indexOf(id) === index);
}

/**
 * @param {any} cart
 */
function codesFromCart(cart) {
  const fromPayload = Array.isArray(cart?.discount_codes)
    ? cart.discount_codes
        .filter((entry) => entry?.applicable !== false && entry?.code)
        .map((entry) => String(entry.code))
    : [];
  if (fromPayload.length) return fromPayload;

  return Array.from(document.querySelectorAll('.cart-discount__pill[data-discount-code]'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.discountCode : ''))
    .filter(Boolean);
}

export async function syncCharmBagDiscount() {
  if (pending) return pending;
  pending = runSync().finally(() => {
    pending = null;
  });
  return pending;
}

async function runSync() {
  if (syncing) return;
  syncing = true;
  try {
    const cartResponse = await fetch(CART_URL, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();
    const items = cart.items || [];
    const wantsGift = items.some(isBagItem) && items.some(isCharmItem);
    const current = codesFromCart(cart);
    const hasCode = current.some((code) => code.toUpperCase() === CODE);
    if (wantsGift === hasCode) return;

    const nextCodes = wantsGift
      ? [...current.filter((code) => code.toUpperCase() !== CODE), CODE]
      : current.filter((code) => code.toUpperCase() !== CODE);

    const sections = sectionIds();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: nextCodes.join(','),
        sections,
      }),
    });

    const updateResponse = await fetch(CART_UPDATE_URL, {
      ...config,
      credentials: 'same-origin',
    });
    if (!updateResponse.ok) return;
    const data = await updateResponse.json();

    if (data.sections) {
      for (const id of sections) {
        if (data.sections[id]) {
          await morphSection(id, data.sections[id], {
            mode: document.querySelector('theme-drawer cart-items-component') ? 'hydration' : 'full',
          });
        }
      }
    }
  } catch (error) {
    console.warn('[charm-bag-discount]', error);
  } finally {
    syncing = false;
  }
}

function shouldWatch(input) {
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input || '');
  return /\/cart\/(add|change|clear|update)\.js/i.test(url);
}

const originalFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  if (syncing) return response;
  if (shouldWatch(args[0])) {
    window.setTimeout(() => {
      void syncCharmBagDiscount();
    }, 50);
  }
  return response;
};

document.addEventListener('DOMContentLoaded', () => {
  void syncCharmBagDiscount();
});

window.kbSyncCharmBagDiscount = syncCharmBagDiscount;
